import React from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import useCities from 'shared/hooks/useCities';
import { buildFunnelUrl } from 'static_pages/marketing_pages/helpers';
import Container from 'system/components/Container';

function HowItWorksCTA({
  cta,
  cateringCategories,
  cityFromSlug,
  ...restProps
}) {
  const { t } = useTranslation('dynamic_landing_page');
  const { cities, cityKeys } = useCities();
  const cityKey = cityKeys.find((cityKey) => cities[cityKey] === cityFromSlug);
  const router = useRouter();
  return (
    <Box
      as="section"
      pt={8}
      pb={{ xs: 4, md: 6 }}
      my={{ xs: 2, md: 4 }}
      bgcolor="#F9F9F4"
      {...restProps}
    >
      <Container size="sm">
        <Box maxWidth="730px" px={2} mb={3} mx="auto" textAlign="center">
          <Typography
            variant="h3"
            lineHeight={'150%'}
            fontSize="24px"
            component="h3"
          >
            {t('howItWorks.title')}
          </Typography>
        </Box>
        <HowItWorksContent>
          <HowItWorksItem>
            <IconWrapper>
              <Image
                alt=""
                src={'/artifacts/images/marketing_pages/howItWorks_1.svg'}
                width={120}
                height={120}
              />
            </IconWrapper>
            <Box flex={1} sm={{ textAlign: 'center' }} pb={{ xs: 3, md: 4 }}>
              <TypographyItemTitle variant="subtitle1" component="p" pb={1}>
                {t('howItWorks.part1')}
              </TypographyItemTitle>
              <TypographyItemSubtitle variant="body1">
                {t('howItWorks.subtitle1')}
              </TypographyItemSubtitle>
            </Box>
          </HowItWorksItem>

          <HowItWorksItem>
            <IconWrapper>
              <Image
                alt=""
                src={'/artifacts/images/marketing_pages/howItWorks_2.svg'}
                width={120}
                height={120}
              />
            </IconWrapper>

            <Box flex={1} sm={{ textAlign: 'center' }} pb={{ xs: 3, md: 4 }}>
              <TypographyItemTitle variant="subtitle1" component="p" pb={1}>
                {t('howItWorks.part2')}
              </TypographyItemTitle>

              <TypographyItemSubtitle variant="body1">
                {t('howItWorks.subtitle2')}
              </TypographyItemSubtitle>
            </Box>
          </HowItWorksItem>
          <HowItWorksItem>
            <IconWrapper>
              <Image
                alt=""
                src={'/artifacts/images/marketing_pages/howItWorks_3.svg'}
                width={120}
                height={120}
              />
            </IconWrapper>

            <Box flex={1} sm={{ textAlign: 'center' }} pb={{ xs: 3, md: 4 }}>
              <TypographyItemTitle variant="subtitle1" component="p" pb={1}>
                {t('howItWorks.part3')}
              </TypographyItemTitle>

              <TypographyItemSubtitle variant="body1">
                {t('howItWorks.subtitle3')}
              </TypographyItemSubtitle>
            </Box>
          </HowItWorksItem>
        </HowItWorksContent>
        <Box pt={2} textAlign="center">
          <CTAButton
            variant="primary"
            size="lg"
            rounded
            id="how-it-works-button"
            as="a"
            href={buildFunnelUrl({
              city: cityKey,
              cateringCategories,
              router,
            })}
            targe="_blank"
          >
            {cta}
          </CTAButton>
        </Box>
      </Container>
    </Box>
  );
}

function IconWrapper({ children }) {
  return (
    <Box mb={{ xs: 0, md: 0, lg: 2 }} display="flex" justifyContent="center">
      {children}
    </Box>
  );
}

const CTAButton = styled(Button)`
  min-height: 42px;
  display: inline-grid;
  place-content: center;
  white-space: normal;
`;

const HowItWorksContent = styled.ol`
  list-style: none;
  padding: 0;
  flex-direction: column;
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(3, 0)};
  max-width: 350px;
  margin: auto;
  gap: ${({ theme }) => theme.spacing(1)}px;

  ${(props) => props.theme.breakpoints.up('sm')} {
    gap: ${({ theme }) => theme.spacing(6)}px;
    flex-direction: row;
    max-width: none;
  }
`;

const HowItWorksItem = styled.li`
  width: 25%;
  text-align: center;

  ${(props) => props.theme.breakpoints.down('sm')} {
    width: 33%;
  }

  ${(props) => props.theme.breakpoints.down('xs')} {
    width: 100%;
    display: flex;
    text-align: center;
    flex-direction: column;
  }
`;

const TypographyItemTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  white-space: pre-line;
  margin-top: ${({ theme }) => theme.spacing(1)}px;
`;

const TypographyItemSubtitle = styled(Typography)`
  color: ${({ theme }) => theme.palette.grey.secondary};
  font-size: 16px;
`;

export default HowItWorksCTA;
